import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { selectHypothesisFrame } from '../../../selectors/message';
import {
  selectCartItems,
  selectCartItemsQuantity,
} from '../../../selectors/cart';
import { selectFeatureFlagUnresolvedItems } from '../config/config.selector';
import { CartItemAdditionTypes } from '../../../constants/event';
import {
  CappedQuantity,
  getCappedQuantity,
  normalizeItemName,
} from '../../../utils/unresolvedItems';

const selectUnresolvedItems = (state: RootState) => state.unresolvedItems;

// Raw cumulative lists straight off the latest hypothesis frame (source of
// truth — the producer replaces these every event). Absent fields => empty.
const selectAllNotOnMenuCards = createSelector(
  selectHypothesisFrame,
  (frame) => frame?.data?.not_on_menu_cards ?? []
);

const selectAllInvalidOptionLines = createSelector(
  selectHypothesisFrame,
  (frame) => frame?.data?.invalid_option_lines ?? []
);

const selectAllQuantityEscalationItems = createSelector(
  selectHypothesisFrame,
  (frame) => frame?.data?.quantity_invalid ?? []
);

const selectAllNoDefaultOptionConstraints = createSelector(
  selectHypothesisFrame,
  (frame) => frame?.data?.no_default_option_constraints ?? []
);

const selectAllUnmatchedCoupons = createSelector(
  selectHypothesisFrame,
  (frame) => frame?.data?.unmatched_coupons ?? []
);

// Cards visible to the agent — empty when the panel has been dismissed.
const selectVisibleNotOnMenuCards = createSelector(
  selectAllNotOnMenuCards,
  selectUnresolvedItems,
  (cards, { allDismissed }) => (allDismissed ? [] : cards)
);

const selectVisibleInvalidOptionLines = createSelector(
  selectAllInvalidOptionLines,
  selectUnresolvedItems,
  (lines, { allDismissed }) => (allDismissed ? [] : lines)
);

const selectVisibleQuantityEscalationItems = createSelector(
  selectAllQuantityEscalationItems,
  selectUnresolvedItems,
  (items, { allDismissed }) => (allDismissed ? [] : items)
);

const selectVisibleNoDefaultOptionConstraints = createSelector(
  selectAllNoDefaultOptionConstraints,
  selectUnresolvedItems,
  (entries, { allDismissed }) => (allDismissed ? [] : entries)
);

const selectVisibleUnmatchedCoupons = createSelector(
  selectAllUnmatchedCoupons,
  selectUnresolvedItems,
  (coupons, { allDismissed }) => (allDismissed ? [] : coupons)
);

const selectUnresolvedItemsCount = createSelector(
  selectVisibleNotOnMenuCards,
  selectVisibleInvalidOptionLines,
  selectVisibleQuantityEscalationItems,
  selectVisibleNoDefaultOptionConstraints,
  selectVisibleUnmatchedCoupons,
  (cards, lines, qtyItems, noDefaults, coupons) =>
    cards.length +
    lines.length +
    qtyItems.length +
    noDefaults.length +
    coupons.length
);

// HITL-1621: first-appearance utterance anchors + transcript pulse target.
const selectUnresolvedItemAnchors = createSelector(
  selectUnresolvedItems,
  ({ anchors }) => anchors
);

const selectHighlightedTranscriptMessageId = createSelector(
  selectUnresolvedItems,
  ({ highlightedTranscriptMessageId }) => highlightedTranscriptMessageId
);

export interface CappedQuantityFlag extends CappedQuantity {
  itemName: string; // normalized producer item name, for the panel's reverse lookup
}

// HITL-1621: cartItemId → capped-quantity flag, for the cart card badge
// ("Customer asked for 30 — capped at 12"). The producer's item_name matches the
// cart line's name (case-insensitively), but the cart can hold several lines of
// the same item — so the name is resolved to ONE cartItemId here: when more than
// one line matches, the one whose quantity equals the clamp wins, and if that is
// still ambiguous no line is flagged (a wrong badge sends the agent to fix the
// wrong line). Derived from the ALL list, not the visible one: dismissing the
// panel acknowledges the cards, but the cart is still silently reduced and the
// badge is the only remaining trace (review round 2).
const selectCappedQuantityFlagsByCartItemId = createSelector(
  selectAllQuantityEscalationItems,
  selectFeatureFlagUnresolvedItems,
  selectCartItems,
  selectCartItemsQuantity,
  (
    items,
    enabled,
    cartItems,
    quantities
  ): Record<string, CappedQuantityFlag> => {
    if (!enabled) return {};
    const flags: Record<string, CappedQuantityFlag> = {};
    items.forEach((item) => {
      const capped = getCappedQuantity(item);
      if (!capped) return;
      const itemName = normalizeItemName(item.item_name);
      // HITL-1685: only lines the AI put in the cart. The flag says the AI
      // reduced this quantity, which cannot be true of a line the agent
      // authored — a takeover's promoted lines, or a same-named item the
      // agent added — so matching by name alone pinned it on their work.
      let matches = Object.values(cartItems ?? {}).filter(
        (cartItem) =>
          cartItem.addedBy !== CartItemAdditionTypes.human &&
          normalizeItemName(cartItem.name) === itemName
      );
      if (matches.length > 1) {
        matches = matches.filter(
          (cartItem) =>
            (quantities?.[cartItem.cartItemId] ?? 1) === capped.cappedTo
        );
      }
      if (matches.length === 1) {
        flags[String(matches[0].cartItemId)] = { ...capped, itemName };
      }
    });
    return flags;
  }
);

export {
  selectAllNotOnMenuCards,
  selectAllInvalidOptionLines,
  selectAllQuantityEscalationItems,
  selectAllNoDefaultOptionConstraints,
  selectAllUnmatchedCoupons,
  selectVisibleNotOnMenuCards,
  selectVisibleInvalidOptionLines,
  selectVisibleQuantityEscalationItems,
  selectVisibleNoDefaultOptionConstraints,
  selectVisibleUnmatchedCoupons,
  selectUnresolvedItemsCount,
  selectUnresolvedItemAnchors,
  selectHighlightedTranscriptMessageId,
  selectCappedQuantityFlagsByCartItemId,
};
